import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGql, formatDateTime, Card, CardContent, Separator } from '@ah/design';
import { Header, DetailSkeleton, PullToRefresh, SaveButton } from '@ah/design/mobile';
import { Clock, Podcast, Play, Pause, Rewind, FastForward } from 'lucide-react';
import {
  PODCAST_EPISODE_DETAIL_QUERY,
  RECORD_EPISODE_ENGAGEMENT_MUTATION,
  RECORD_EPISODE_SAVE_MUTATION,
  RECORD_EPISODE_UNSAVE_MUTATION,
  type EpisodeDetail,
  type EpisodeSummary,
} from '../shared/index.ts';
import { formatDuration, formatTimestamp, ErrorRetry } from './_parts.tsx';

export function PodcastEpisode({ inSheet }: { inSheet?: boolean } = {}) {
  const gql = useGql();
  const qc = useQueryClient();
  const { id = '' } = useParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['podcasts', 'episode', id],
    queryFn: () => gql.request<{ podcastEpisode: EpisodeDetail | null }>(PODCAST_EPISODE_DETAIL_QUERY, { id }),
  });

  useEffect(() => {
    if (id) gql.request(RECORD_EPISODE_ENGAGEMENT_MUTATION, { id }).catch(() => {});
  }, [id, gql]);

  const body = isLoading ? (
    <DetailSkeleton hero="square" />
  ) : error || !data?.podcastEpisode ? (
    <ErrorRetry label="Episode not found." onRetry={() => refetch()} />
  ) : (
    <EpisodeBody ep={data.podcastEpisode} />
  );

  if (inSheet) return <>{body}</>;

  const trailing = data?.podcastEpisode ? (
    <SaveButton
      isSaved={data.podcastEpisode.isSaved}
      onSave={async () => {
        await gql.request(RECORD_EPISODE_SAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['podcasts', 'saved'] });
        qc.setQueryData(['podcasts', 'episode', id], (prev: { podcastEpisode: EpisodeDetail | null } | undefined) =>
          prev?.podcastEpisode ? { podcastEpisode: { ...prev.podcastEpisode, isSaved: true } } : prev,
        );
      }}
      onUnsave={async () => {
        await gql.request(RECORD_EPISODE_UNSAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['podcasts', 'saved'] });
        qc.setQueryData(['podcasts', 'episode', id], (prev: { podcastEpisode: EpisodeDetail | null } | undefined) =>
          prev?.podcastEpisode ? { podcastEpisode: { ...prev.podcastEpisode, isSaved: false } } : prev,
        );
      }}
    />
  ) : null;

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title={data?.podcastEpisode?.name ?? 'Episode'} backTo="/podcasts" trailing={trailing} />
      <PullToRefresh className="flex-1" onRefresh={() => refetch()}>
        {body}
      </PullToRefresh>
    </div>
  );
}

function EpisodeBody({ ep }: { ep: EpisodeDetail }) {
  const duration = formatDuration(ep.duration);
  const image = ep.imageUrl ?? ep.series?.imageUrl ?? null;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playerWrapRef = useRef<HTMLDivElement | null>(null);
  const seekTo = (sec: number) => {
    const el = audioRef.current;
    if (!el) return;
    try {
      el.currentTime = sec;
      void el.play();
      playerWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {}
  };
  return (
    <article className="pb-10 space-y-5">
      <div ref={playerWrapRef} className="px-4 py-6 bg-muted/40 flex justify-center">
        <CoverPlayer image={image} audioUrl={ep.audioUrl} audioRef={audioRef} />
      </div>

      <div className="px-4 space-y-5">
      <header className="space-y-2">
        {ep.series ? (
          <Link
            to={`/podcasts/series/${ep.series.id}`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Podcast size={14} /> {ep.series.name}
            {ep.series.author ? <span> · {ep.series.author}</span> : null}
          </Link>
        ) : null}
        <h1 className="text-xl font-bold leading-tight">{ep.name}</h1>
        <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
          {duration ? <span>{duration}</span> : null}
          {ep.datePublished ? (
            <span className="inline-flex items-center gap-1">
              <Clock size={11} /> {formatDateTime(ep.datePublished)}
            </span>
          ) : null}
        </div>
        {ep.summary?.topics?.length ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {ep.summary.topics.slice(0, 6).map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wide rounded-full border px-2 py-0.5 text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </header>

      {ep.summary ? <SummaryBody summary={ep.summary} onSeek={ep.audioUrl ? seekTo : null} /> : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            {ep.summaryStatus === 'failed'
              ? 'Summary generation failed for this episode.'
              : ep.transcriptionStatus === 'ineligible'
                ? 'This episode is not eligible for transcription.'
                : ep.transcriptionStatus === 'failed'
                  ? 'Transcription failed for this episode.'
                  : 'This episode is queued for transcription. Check back in a moment.'}
          </CardContent>
        </Card>
      )}

      </div>
    </article>
  );
}

function SummaryBody({
  summary,
  onSeek,
}: {
  summary: EpisodeSummary;
  onSeek: ((sec: number) => void) | null;
}) {
  return (
    <div className="space-y-5">
      {summary.hook ? (
        <p className="text-base font-medium leading-relaxed text-muted-foreground">{summary.hook}</p>
      ) : null}

      {summary.whatItsAbout ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">In Brief</h2>
          <p className="text-sm leading-relaxed">{summary.whatItsAbout}</p>
        </section>
      ) : null}

      {summary.longRead.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Full Story</h2>
          <div className="space-y-3 text-sm leading-relaxed">
            {summary.longRead.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </section>
      ) : null}

      {summary.whyItMatters ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Why it matters</h2>
          <p className="text-sm leading-relaxed">{summary.whyItMatters}</p>
        </section>
      ) : null}

      {summary.keyIdeas.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key ideas</h2>
          <div className="space-y-3">
            {summary.keyIdeas.map((idea, i) => (
              <div key={i}>
                <h3 className="text-sm font-semibold">{idea.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{idea.body}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {summary.keyQuotes.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key quotes</h2>
          <div className="space-y-3">
            {summary.keyQuotes.map((q, i) => {
              const ts = formatTimestamp(q.timestampSec);
              const canSeek = onSeek && q.timestampSec != null;
              return (
                <blockquote key={i} className="border-l-2 border-muted pl-3">
                  <p className="italic text-sm">"{q.quote}"</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    — {q.speaker}
                    {ts ? (
                      canSeek ? (
                        <>
                          {' · '}
                          <button
                            type="button"
                            onClick={() => onSeek!(q.timestampSec!)}
                            className="underline tabular-nums"
                          >
                            {ts}
                          </button>
                        </>
                      ) : (
                        <span> · {ts}</span>
                      )
                    ) : null}
                  </p>
                </blockquote>
              );
            })}
          </div>
        </section>
      ) : null}

      {summary.keyPeople.length > 0 ? (
        <section>
          <h2 className="text-lg font-semibold mb-2">Key people</h2>
          <ul className="space-y-1.5">
            {summary.keyPeople.map((p, i) => (
              <li key={i} className="text-sm">
                <span className="font-medium">{p.name}</span>
                {p.role ? <span className="text-muted-foreground"> — {p.role}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {summary.chapters.length > 0 ? (
        <>
          <Separator />
          <section>
            <h2 className="text-lg font-semibold mb-2">Chapters</h2>
            <ol className="space-y-2">
              {summary.chapters.map((c, i) => {
                const ts = formatTimestamp(c.startSec);
                const content = (
                  <>
                    <p className="font-medium">
                      {ts ? <span className="tabular-nums text-muted-foreground mr-2">{ts}</span> : null}
                      {c.title}
                    </p>
                    {c.body ? (
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{c.body}</p>
                    ) : null}
                  </>
                );
                return (
                  <li key={i} className="text-sm">
                    {onSeek ? (
                      <button
                        type="button"
                        onClick={() => onSeek(c.startSec)}
                        className="w-full text-left py-1 rounded hover:bg-muted/50 active:bg-muted"
                      >
                        {content}
                      </button>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        </>
      ) : null}
    </div>
  );
}

function CoverPlayer({
  image,
  audioUrl,
  audioRef,
}: {
  image: string | null;
  audioUrl: string | null;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
}) {
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  };
  const skip = (delta: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min((el.duration || dur) - 0.1, el.currentTime + delta));
  };
  const scrub = (sec: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = sec;
    setCur(sec);
  };

  const hasAudio = !!audioUrl;
  const pct = dur > 0 ? (cur / dur) * 100 : 0;

  return (
    <div className="w-full max-w-sm">
      <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-muted shadow-lg select-none">
        {image ? (
          <img
            src={image}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
              started ? 'scale-110 blur-md brightness-75' : ''
            }`}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-muted-foreground opacity-40">
            <Podcast size={64} />
          </div>
        )}

        {started && image ? (
          <img
            src={image}
            alt=""
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[58%] w-1/2 aspect-square object-cover rounded-lg shadow-2xl"
          />
        ) : null}

        {hasAudio && !started ? (
          <button
            type="button"
            onClick={toggle}
            aria-label="Play"
            className="absolute inset-0 flex items-center justify-center focus:outline-none"
          >
            <span className="grid place-items-center rounded-full bg-white/95 text-black shadow-xl w-20 h-20">
              <Play size={32} fill="currentColor" className="ml-0.5" />
            </span>
          </button>
        ) : null}

        {hasAudio && started ? (
          <div className="absolute inset-0 flex items-center justify-center gap-6 text-white">
            <button
              type="button"
              onClick={() => skip(-15)}
              aria-label="Back 15 seconds"
              className="grid place-items-center w-11 h-11 rounded-full bg-black/40 active:bg-black/60"
            >
              <Rewind size={20} fill="currentColor" />
            </button>
            <button
              type="button"
              onClick={toggle}
              aria-label={playing ? 'Pause' : 'Play'}
              className="grid place-items-center w-16 h-16 rounded-full bg-white/95 text-black shadow-xl"
            >
              {playing ? (
                <Pause size={26} fill="currentColor" />
              ) : (
                <Play size={26} fill="currentColor" className="ml-0.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => skip(30)}
              aria-label="Forward 30 seconds"
              className="grid place-items-center w-11 h-11 rounded-full bg-black/40 active:bg-black/60"
            >
              <FastForward size={20} fill="currentColor" />
            </button>
          </div>
        ) : null}

        {started ? (
          <div className="absolute left-0 right-0 bottom-0 px-4 pb-3 pt-8 bg-gradient-to-t from-black/70 to-transparent text-white">
            <input
              type="range"
              min={0}
              max={dur || 0}
              step={0.1}
              value={cur}
              onChange={(e) => scrub(Number(e.target.value))}
              aria-label="Scrub"
              className="w-full accent-white h-1"
              style={{
                background: `linear-gradient(to right, rgba(255,255,255,0.9) ${pct}%, rgba(255,255,255,0.25) ${pct}%)`,
              }}
            />
            <div className="flex justify-between text-[11px] tabular-nums opacity-90 mt-1">
              <span>{fmtSec(cur)}</span>
              <span>-{fmtSec(Math.max(0, dur - cur))}</span>
            </div>
          </div>
        ) : null}
      </div>

      {hasAudio ? (
        <audio
          ref={audioRef}
          src={audioUrl!}
          preload="metadata"
          onPlay={() => {
            setPlaying(true);
            setStarted(true);
          }}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
          onDurationChange={(e) => setDur(e.currentTarget.duration || 0)}
          onEnded={() => setPlaying(false)}
        />
      ) : null}
    </div>
  );
}

function fmtSec(s: number) {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}:${String(m % 60).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${m}:${String(r).padStart(2, '0')}`;
}
